import { PDFDocument, StandardFonts } from 'pdf-lib';

/**
 * Merge multiple PDF or image files into one single PDF
 * @param {File[]} allFiles - array of File objects (images or PDFs)
 * @returns {Promise<File>} merged PDF File
 */
export async function createMergedPdf(allFiles) {
    const mergedPdf = await PDFDocument.create();

    for (const file of allFiles) {
        const arrayBuffer = await file.arrayBuffer();
        const fileType = file.type;

        if (fileType === 'application/pdf') {
            // Merge existing PDF pages
            const pdf = await PDFDocument.load(arrayBuffer);
            const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
            copiedPages.forEach((page) => mergedPdf.addPage(page));
        } else if (fileType.startsWith('image/')) {
            // Add image as new PDF page
            const imgBytes = arrayBuffer;
            let img;
            // Detect by magic bytes: the extension/MIME can lie (e.g. a JPEG named .png)
            const head = new Uint8Array(imgBytes.slice(0, 4));
            const isPng = head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47;
            if (isPng) img = await mergedPdf.embedPng(imgBytes);
            else img = await mergedPdf.embedJpg(imgBytes);

            const page = mergedPdf.addPage();
            const { width, height } = img.scale(1);

            // Fit image nicely to A4 page
            const pageWidth = 595.28; // A4 width in points
            const pageHeight = 841.89; // A4 height in points
            const scale = Math.min(pageWidth / width, pageHeight / height);
            const x = (pageWidth - width * scale) / 2;
            const y = (pageHeight - height * scale) / 2;

            page.setSize(pageWidth, pageHeight);
            page.drawImage(img, { x, y, width: width * scale, height: height * scale });
        }
    }

    const pdfBytes = await mergedPdf.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const mergedFile = new File([blob], 'merged_documents.pdf', { type: 'application/pdf' });
    return mergedFile;
}
